import { db, storage } from '../firebase';
import { collection, addDoc, getDocs, getDoc, doc, setDoc, deleteDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const API = process.env.REACT_APP_BACKEND_URL;

// Authoritative backend POST. Returns true on HTTP 2xx, else throws.
async function notifyBackend(endpoint, data) {
  const res = await fetch(`${API}/api/notify/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Backend submission failed (${res.status}): ${txt}`);
  }
  return true;
}

// Best-effort Firestore write — never throws to caller.
async function safeFirestoreWrite(fn) {
  try {
    return await fn();
  } catch (e) {
    console.warn('Firestore write skipped (non-blocking):', e?.message || e);
    return null;
  }
}

// Best-effort Firebase Storage upload — returns null on failure.
async function safeStorageUpload(path, file) {
  try {
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  } catch (e) {
    console.warn('Storage upload skipped (non-blocking):', e?.message || e);
    return null;
  }
}

export async function submitEnquiry(data, file) {
  const fileUrl = file ? await safeStorageUpload(`rfq/${Date.now()}_${file.name}`, file) : null;
  await safeFirestoreWrite(() =>
    addDoc(collection(db, 'enquiries'), { ...data, rfqUrl: fileUrl, rfqFileName: file?.name || null, status: 'new', createdAt: serverTimestamp() })
  );
  // Backend persistence + email — authoritative source of truth.
  await notifyBackend('enquiry', { ...data, rfqFileName: file?.name || null });
  return { ok: true };
}

export async function submitCareer(data) {
  return safeFirestoreWrite(() =>
    addDoc(collection(db, 'careers'), { ...data, status: 'new', createdAt: serverTimestamp() })
  );
}

export async function fetchEnquiries() {
  const q = query(collection(db, 'enquiries'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function fetchCareers() {
  const q = query(collection(db, 'careers'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function fetchSolarSurveys() {
  const q = query(collection(db, 'solar_surveys'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function fetchEnergyAudits() {
  const q = query(collection(db, 'energy_audits'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function fetchSubcontractors() {
  const q = query(collection(db, 'subcontractor_registrations'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function fetchSiteContent() {
  try {
    const r = doc(db, 'siteContent', 'main');
    const snap = await getDoc(r);
    return snap.exists() ? snap.data() : null;
  } catch (e) { return null; }
}

export async function saveSiteContent(data) {
  const r = doc(db, 'siteContent', 'main');
  return setDoc(r, { ...data, updatedAt: serverTimestamp() }, { merge: true });
}

export async function deleteEnquiry(id) { return deleteDoc(doc(db, 'enquiries', id)); }
export async function deleteCareer(id) { return deleteDoc(doc(db, 'careers', id)); }

export async function submitSolarSurvey(data) {
  await safeFirestoreWrite(() =>
    addDoc(collection(db, 'solar_surveys'), { ...data, status: 'new', createdAt: serverTimestamp() })
  );
  await notifyBackend('solar-survey', { data });
  return { ok: true };
}

export async function submitEnergyAudit(data) {
  await safeFirestoreWrite(() =>
    addDoc(collection(db, 'energy_audits'), { ...data, status: 'new', createdAt: serverTimestamp() })
  );
  await notifyBackend('energy-audit', { data });
  return { ok: true };
}

export async function submitCsatSurvey(data) {
  await safeFirestoreWrite(() =>
    addDoc(collection(db, 'csat_surveys'), { ...data, status: 'new', createdAt: serverTimestamp() })
  );
  await notifyBackend('csat-survey', { data });
  return { ok: true };
}

export async function fetchCsatSurveys() {
  const q = query(collection(db, 'csat_surveys'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function submitSubcontractor(formData, files) {
  const fileUrls = {};
  const documentNames = {};
  for (const [key, file] of Object.entries(files || {})) {
    const url = await safeStorageUpload(`subcontractor/${Date.now()}_${file.name}`, file);
    if (url) fileUrls[key] = { url, name: file.name };
    documentNames[key] = file.name;
  }
  await safeFirestoreWrite(() =>
    addDoc(collection(db, 'subcontractor_registrations'), {
      ...formData,
      documents: fileUrls,
      status: 'new',
      createdAt: serverTimestamp(),
    })
  );
  await notifyBackend('subcontractor', { data: { ...formData, documentNames } });
  return { ok: true };
}
